import React, { useMemo } from "react";

import BadgeView from "./BadgeView";

import colors from "@/config/colors";
import { CurrencyCode, SortField } from "@/generated/graphql";
import { useTheme } from "@/providers/ThemeProvider";
import getColorClass from "@/utils/colorScale";
import formatCurrencyShort from "@/utils/formatCurrencyShort";

/**
 * Container component for badge display with color-coded values
 * @param root0 - Component props
 * @param root0.type - Type of metric being displayed (e.g., FitScore, PlayerValuation)
 * @param root0.value - The numeric value to display in the badge
 * @param root0.min - Minimum value for color scale calculation
 * @param root0.max - Maximum value for color scale calculation
 * @param root0.currencyCode - Currency code for formatting valuation values
 * @param root0.isHigherBetter - Whether higher values should be displayed as better (affects color coding)
 * @param root0.isDefaultStyle - Whether to use default neutral styling instead of color-coded styling
 */
const BadgeContainer = ({
  type,
  value,
  min,
  max,
  currencyCode,
  isHigherBetter,
  isDefaultStyle,
}: {
  type?: SortField;
  value: number | null | undefined;
  min: number;
  max: number;
  currencyCode?: CurrencyCode;
  isHigherBetter: boolean;
  isDefaultStyle?: boolean;
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const transformedValue = useMemo(() => {
    if (value && type === SortField.FitScore) return value * 100;
    return value;
  }, [type, value]);
  const formattedValue = useMemo(() => {
    if ((!transformedValue && transformedValue !== 0) || !value) return "NA";
    if (type === SortField.FitScore) return `${Math.round(transformedValue)}`;
    if (type === SortField.PlayerValuation)
      return formatCurrencyShort(value, currencyCode ?? CurrencyCode.Eur);
    return Math.round(transformedValue).toString();
  }, [transformedValue, value, type, currencyCode]);

  const colorClass = useMemo(
    () =>
      transformedValue
        ? getColorClass(
            isDefaultStyle ? undefined : (transformedValue ?? undefined),
            min,
            max,
            isHigherBetter,
            true,
            isDark
          )
        : {
            background: isDark
              ? colors.statusBadge.default.background[400]
              : "#F4F4F5", // zinc-100 for light
            border: isDark ? colors.statusBadge.default.border[400] : "#D4D4D8", // zinc-300 for light
            text: isDark ? colors.statusBadge.default.text[400] : "#3F3F46", // zinc-700 for light
          },
    [transformedValue, isDefaultStyle, min, max, isHigherBetter, isDark]
  );

  return <BadgeView colorClass={colorClass} formattedValue={formattedValue} />;
};

export default React.memo(BadgeContainer);
